import React, { useState } from 'react';

import { RxMagicWand } from 'react-icons/rx';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import TeamMembersModal from './TeamMembersModal';
import ThematicGroupsMobile from './ThematicGroupsMobile';
import useThematicGroups from '../hooks/useThematicGroups';
import { SortField, Team } from '../types/thematicGroups';
import { useTheme as useThemeContext } from 'hooks/useTheme';
type Props = {
  userId: string;
  loggedUserId: string;
};

const AccordionThematicGroups: React.FC<Props> = ({ userId, loggedUserId }) => {
  const theme = useThemeContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const isOwnProfile = userId === loggedUserId;

  const { sortedTeams, loading, sortConfig, setSortConfig, commonTeams, otherTeams } =
    useThematicGroups({
      userUuid1: userId,
      userUuid2: loggedUserId,
    });

  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showAllTeams, setShowAllTeams] = useState(false);

  const handleSort = (field: SortField) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';

    setSortConfig({
      field,
      direction: newDirection,
    });
  };

  const handleOpenMembersModal = (team: Team) => {
    setSelectedTeam(team);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTeam(null);
  };

  const isCommonTeam = (team: Team) => {
    return commonTeams.some(commonTeam => commonTeam.uuid === team.uuid);
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) {
      return <KeyboardArrowDownIcon sx={{ opacity: 0.3 }} />;
    }
    return sortConfig.direction === 'asc' ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />;
  };

  const commonTeamsCount = commonTeams.length;

  const teamsToShow = showAllTeams ? sortedTeams : commonTeams;
  const hasOtherTeams = otherTeams.length > 0;

  return (
    <>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              Grupos Temáticos
            </Typography>
            {commonTeamsCount > 0 && !isOwnProfile && (
              <Chip
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <span>Em comum:</span>
                    <RxMagicWand size={12} />
                    <span>{commonTeamsCount}</span>
                  </Box>
                }
                size="small"
                sx={{
                  backgroundColor: theme.theme.primary,
                  color: theme.theme.background.primary,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  marginRight: 1,
                }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Carregando grupos temáticos...</Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>
              {isMobile ? (
                <ThematicGroupsMobile
                  teams={teamsToShow}
                  commonTeams={commonTeams}
                  onOpenMembersModal={handleOpenMembersModal}
                />
              ) : (
                <Box sx={{ width: '100%' }}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      gap: 2,
                      py: 0,
                      px: 2,
                      mb: 1,
                      borderRadius: 1,
                      backgroundColor: theme.theme.background.primary,
                    }}
                  >
                    <Box
                      onClick={() => handleSort('name')}
                      sx={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        Nome
                      </Typography>
                      {getSortIcon('name')}
                    </Box>
                    <Box
                      onClick={() => handleSort('total_members')}
                      sx={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        width="100%"
                        textAlign="center"
                      >
                        Total de membros
                      </Typography>
                      {getSortIcon('total_members')}
                    </Box>
                    <Box
                      onClick={() => handleSort('in_common')}
                      sx={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        width="100%"
                        textAlign="end"
                      >
                        Em comum
                      </Typography>
                      {getSortIcon('in_common')}
                    </Box>
                  </Box>

                  {/* Body */}
                  <Stack spacing={1}>
                    {teamsToShow.map(team => {
                      const isCommon = isCommonTeam(team);
                      const commonConnections = team.in_common_connections;
                      const totalCommon = commonConnections.length;

                      return (
                        <Box
                          key={team.uuid}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr',
                            gap: 1,
                            py: 1,
                            px: 2,
                            borderRadius: 2,
                            backgroundColor: theme.theme.background.primary,
                            borderLeft:
                              isCommon && !isOwnProfile
                                ? `2px solid ${theme.theme.primary}`
                                : 'none',
                            '&:hover': {
                              backgroundColor: theme.theme.background.primary,
                              transform: 'translateY(-1px)',
                              transition: 'all 0.2s ease',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {/* Nome */}
                          <Box display="flex" alignItems="center" gap={1}>
                            {isCommon && !isOwnProfile && (
                              <RxMagicWand color={theme.theme.primary} size={16} />
                            )}
                            <Typography variant="body2" fontWeight={isCommon ? 'bold' : 'normal'}>
                              {team.name}
                            </Typography>
                          </Box>

                          {/* Total de membros */}
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Typography variant="body2">{team.total_members}</Typography>
                            <IconButton
                              onClick={() => handleOpenMembersModal(team)}
                              size="small"
                              sx={{
                                color: theme.theme.primary,
                                '&:hover': {
                                  backgroundColor: theme.theme.primary + '20',
                                },
                              }}
                            >
                              <VisibilityIcon
                                sx={{
                                  color: theme.theme.primary,
                                  fontSize: 16,
                                }}
                              />
                            </IconButton>
                          </Box>

                          {/* Em comum */}
                          <Box display="flex" alignItems="center" justifyContent="end" gap={1}>
                            {totalCommon > 0 ? (
                              <>
                                <AvatarGroup
                                  max={6}
                                  sx={{ '& .MuiAvatar-root': { width: 24, height: 24 } }}
                                >
                                  {commonConnections.slice(0, 6).map(connection => (
                                    <Avatar
                                      key={connection.id}
                                      src={connection.photo_url}
                                      alt={connection.name}
                                      sx={{ width: 24, height: 24 }}
                                    >
                                      {connection.name.charAt(0)}
                                    </Avatar>
                                  ))}
                                </AvatarGroup>
                                {totalCommon > 6 && (
                                  <Chip
                                    label={`+${totalCommon - 6}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: theme.theme.primary,
                                      color: theme.theme.background.primary,
                                      fontSize: '0.75rem',
                                      height: 20,
                                      minWidth: 20,
                                    }}
                                  />
                                )}
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Nenhuma conexão em comum
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {hasOtherTeams && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Box
                    component="button"
                    onClick={() => setShowAllTeams(!showAllTeams)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 3,
                      py: 1.5,
                      border: `1px solid ${theme.theme.primary}`,
                      borderRadius: 2,
                      backgroundColor: showAllTeams ? theme.theme.primary : 'transparent',
                      color: showAllTeams ? theme.theme.background.primary : theme.theme.primary,
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      '&:hover': {
                        backgroundColor: showAllTeams
                          ? theme.theme.primary + 'CC'
                          : theme.theme.primary,
                        color: theme.theme.background.primary,
                      },
                    }}
                  >
                    <VisibilityIcon sx={{ fontSize: 16 }} />
                    {showAllTeams ? 'Ver Apenas Em Comum' : 'Ver Outros Grupos'}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <TeamMembersModal
        open={modalOpen}
        onClose={handleCloseModal}
        team={selectedTeam}
        loggedUserId={loggedUserId}
      />
    </>
  );
};

export default AccordionThematicGroups;
